import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils/dateFormatting';

const API = process.env.REACT_APP_BACKEND_URL;

const PolicyPage = () => {
  const { docType: paramDocType } = useParams();
  const location = useLocation();
  // Derive docType from either param or path
  const docType = paramDocType || (location.pathname === '/privacy' ? 'privacy' : location.pathname === '/eula' ? 'eula' : null);
  const [docInfo, setDocInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const label = docType === 'privacy' ? 'Privacy Policy' : docType === 'eula' ? 'End User License Agreement' : 'Document';

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/api/legal/${docType}`);
        setDocInfo(res.data);
      } catch {
        setError('Document not available');
      } finally {
        setLoading(false);
      }
    };
    if (docType === 'privacy' || docType === 'eula') {
      fetchInfo();
    } else {
      setError('Invalid document type');
      setLoading(false);
    }
  }, [docType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="policy-loading">
        <div className="animate-pulse text-zinc-400">Loading...</div>
      </div>
    );
  }

  if (error || !docInfo?.exists) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4" data-testid="policy-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">{label}</h1>
          <p className="text-zinc-400">This document has not been uploaded yet.</p>
        </div>
        <a href="/" className="text-[#ffd108] hover:underline text-sm">Back to app</a>
      </div>
    );
  }

  const pdfUrl = `${API}/api/legal/${docType}/pdf`;

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="policy-page">
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="text-[#ffd108] font-bold text-lg tracking-tight">EQUIPR CORE</a>
          <span className="text-zinc-600">|</span>
          <h1 className="text-white font-medium">{label}</h1>
        </div>
        <div className="flex items-center gap-4">
          {docInfo.updated_at && (
            <span className="text-xs text-zinc-500">
              Last updated: {formatDate(docInfo.updated_at)}
            </span>
          )}
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#ffd108] hover:underline"
            data-testid="policy-download-link"
          >
            Open PDF directly
          </a>
        </div>
      </header>
      <div className="flex-1 relative">
        <object
          data={pdfUrl}
          type="application/pdf"
          className="w-full border-0"
          style={{ height: 'calc(100vh - 65px)' }}
          data-testid="policy-pdf-object"
        >
          <div className="flex flex-col items-center justify-center gap-4 py-20">
            <p className="text-zinc-400 text-sm">Your browser cannot display this PDF inline.</p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#ffd108] text-black rounded-md font-medium text-sm hover:bg-[#ffd108]/90 transition-colors"
            >
              Download PDF
            </a>
          </div>
        </object>
      </div>
    </div>
  );
};

export default PolicyPage;
